import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useSession, useVenueId } from '../lib/session.js';
import { useVenue } from '../hooks/useVenue.js';
import { addServiceDays, currentServiceDate } from '../lib/time.js';

/**
 * Reports.
 *
 * Every figure is bounded on SERVICE date, so a Ramadan suhoor rush counts
 * against the evening it belonged to rather than being split across two
 * calendar days.
 *
 * The turn-time table shows booked against actual side by side, because the gap
 * between them is the number that tells a manager whether the configured turn
 * times match how the room really runs — a single "average turn time" does not.
 *
 * Financial figures sit behind `report:financial`, which a host does not hold.
 */
export function ReportsScreen() {
  const { t } = useTranslation();
  const venueId = useVenueId();
  const venue = useVenue();
  const { can } = useSession();

  const today = venue ? currentServiceDate(venue) : '';
  const [from, setFrom] = useState(() => (today ? addServiceDays(today, -29) : ''));
  const [to, setTo] = useState(today);

  const range = { venue_id: venueId, from: from || today, to: to || today };
  const enabled = Boolean(venue && range.from && range.to);

  const [covers, turnTimes, noShows, sources, guests, cost] = useQueries({
    queries: [
      { queryKey: ['report-covers', range], enabled, queryFn: () => api.get<{ rows: CoverRow[] }>('/api/v1/reports/covers', range) },
      { queryKey: ['report-turn', range], enabled, queryFn: () => api.get<{ rows: TurnRow[] }>('/api/v1/reports/turn-times', range) },
      { queryKey: ['report-noshow', range], enabled, queryFn: () => api.get<{ rows: NoShowRow[] }>('/api/v1/reports/no-shows', range) },
      { queryKey: ['report-sources', range], enabled, queryFn: () => api.get<{ rows: SourceRow[] }>('/api/v1/reports/sources', range) },
      { queryKey: ['report-guests', range], enabled, queryFn: () => api.get<GuestMix>('/api/v1/reports/guests', range) },
      {
        queryKey: ['report-cost', range],
        enabled: enabled && can('report:financial'),
        queryFn: () => api.get<{ rows: CostRow[] }>('/api/v1/reports/whatsapp-cost', range),
      },
    ],
  });

  if (!venue) return <p className="text-sm text-slate-500">{t('app.loading')}</p>;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-2xl font-semibold">{t('reports.title')}</h1>
        <div className="flex items-end gap-2">
          <label className="flex flex-col gap-1 text-xs">
            <span className="uppercase tracking-wide text-slate-500">{t('reports.from')}</span>
            <input
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              className="rounded-md border border-slate-200 px-2 py-1.5 font-mono text-sm"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="uppercase tracking-wide text-slate-500">{t('reports.to')}</span>
            <input
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              className="rounded-md border border-slate-200 px-2 py-1.5 font-mono text-sm"
            />
          </label>
        </div>
      </header>

      <Panel title={t('reports.covers')}>
        <SimpleTable
          head={[t('common.date'), t('reservations.filterShift'), t('reports.covers'), t('preShift.bookings')]}
          rows={(covers.data?.rows ?? []).map((row) => [
            row.service_date,
            row.shift ?? '—',
            row.covers,
            row.bookings,
          ])}
          empty={t('reports.empty')}
        />
      </Panel>

      <Panel title={t('reports.turnTimes')}>
        <SimpleTable
          head={[t('common.partySize'), t('reports.booked'), t('reports.actual'), t('reports.median')]}
          rows={(turnTimes.data?.rows ?? []).map((row) => [
            row.party_size,
            row.booked_minutes,
            row.avg_minutes,
            row.median_minutes,
          ])}
          empty={t('reports.empty')}
        />
      </Panel>

      <Panel title={t('reports.noShows')}>
        <SimpleTable
          head={[t('common.date'), t('reports.noShows'), t('guests.lateCancels'), t('reports.rate')]}
          rows={(noShows.data?.rows ?? []).map((row) => [
            row.service_date,
            row.no_shows,
            row.late_cancels,
            `${row.no_show_rate ?? 0}%`,
          ])}
          empty={t('reports.empty')}
        />
      </Panel>

      <Panel title={t('reports.sources')}>
        <SimpleTable
          head={[t('reservations.filterSource'), t('preShift.bookings'), t('reports.covers'), t('reports.leadTime')]}
          rows={(sources.data?.rows ?? []).map((row) => [
            row.source,
            row.bookings,
            row.covers,
            `${row.avg_lead_hours ?? 0}h`,
          ])}
          empty={t('reports.empty')}
        />
      </Panel>

      <Panel title={t('reports.guests')}>
        <div className="grid grid-cols-2 gap-3">
          <Stat label={t('reports.newGuests')} value={guests.data?.new_guests ?? 0} />
          <Stat label={t('reports.returningGuests')} value={guests.data?.returning_guests ?? 0} />
        </div>

        {/* The counts say whether anyone came back. This says WHO, which is the
            part a manager acts on — a regular is recognised at the door or is
            not, and that is most of the difference between a restaurant and a
            dining room. */}
        {(guests.data?.repeat_guests ?? []).length > 0 ? (
          <div className="mt-4">
            <h3 className="text-sm font-medium text-slate-700">{t('reports.whoReturned')}</h3>
            <SimpleTable
              head={[
                t('reports.guest'),
                t('reports.totalVisits'),
                t('reports.inPeriod'),
                t('reports.noShows'),
              ]}
              rows={(guests.data?.repeat_guests ?? []).map((g) => [
                [g.first_name, g.last_name].filter(Boolean).join(' ') || '—',
                String(g.total_visits),
                String(g.bookings_in_period),
                String(g.no_show_count),
              ])}
              empty={t('reports.empty')}
            />
          </div>
        ) : null}

        {/* Same guest, same night, more than once. The widget refuses this and
            asks them to phone, but staff can create it deliberately — a family
            taking two tables — and it must not go unnoticed on a full night. */}
        {(guests.data?.double_bookings ?? []).length > 0 ? (
          <div className="mt-4">
            <h3 className="text-sm font-medium text-red-700">{t('reports.doubleBookings')}</h3>
            <p className="mt-0.5 text-xs text-slate-600">{t('reports.doubleBookingsHelp')}</p>
            <SimpleTable
              head={[t('reports.guest'), t('common.date'), t('reports.bookings')]}
              rows={(guests.data?.double_bookings ?? []).map((d) => [
                [d.first_name, d.last_name].filter(Boolean).join(' ') || '—',
                d.service_date,
                d.refs.join(', '),
              ])}
              empty={t('reports.empty')}
            />
          </div>
        ) : null}
      </Panel>

      {can('report:financial') ? (
        <Panel title={t('reports.whatsappCost')}>
          <SimpleTable
            head={[t('reports.template'), t('reports.category'), t('reports.billable')]}
            rows={(cost.data?.rows ?? []).map((row) => [
              row.template_name ?? '—',
              row.meta_pricing_category ?? '—',
              row.billable,
            ])}
            empty={t('reports.empty')}
          />
          {/* The row worth noticing: a template charged as marketing costs
              roughly four times a utility one, and Meta re-categorises
              silently. */}
          {(cost.data?.rows ?? []).some(
            (row) => row.meta_pricing_category?.toLowerCase() === 'marketing',
          ) ? (
            <p className="mt-2 rounded bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {t('reports.category')}: marketing
            </p>
          ) : null}
        </Panel>
      ) : null}
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="font-mono text-2xl" data-numeric>
        {value}
      </div>
    </div>
  );
}

function SimpleTable({
  head,
  rows,
  empty,
}: {
  head: string[];
  rows: Array<Array<string | number>>;
  empty: string;
}) {
  if (rows.length === 0) return <p className="text-sm text-slate-500">{empty}</p>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wide text-slate-500">
          <tr>
            {head.map((cell) => (
              <th key={cell} scope="col" className="py-1 text-start">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index} className="border-t border-slate-100">
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className={cellIndex === 0 ? 'py-1.5' : 'py-1.5 font-mono'}
                  data-numeric={cellIndex === 0 ? undefined : true}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface CoverRow { service_date: string; shift: string | null; covers: number; bookings: number }
interface TurnRow { party_size: number; booked_minutes: number; avg_minutes: number; median_minutes: number }
interface NoShowRow { service_date: string; no_shows: number; late_cancels: number; no_show_rate: number | null }
interface SourceRow { source: string; bookings: number; covers: number; avg_lead_hours: number | null }
interface GuestMix {
  new_guests: number;
  returning_guests: number;
  repeat_guests: Array<{
    id: string;
    first_name: string | null;
    last_name: string | null;
    total_visits: number;
    no_show_count: number;
    bookings_in_period: number;
  }>;
  double_bookings: Array<{
    first_name: string | null;
    last_name: string | null;
    service_date: string;
    bookings: number;
    refs: string[];
  }>;
}
interface CostRow { template_name: string | null; meta_pricing_category: string | null; billable: number }
